import React, { useState, useEffect } from "react";
import { message } from "antd";
import {
  DevicePhoneMobileIcon,
  BanknotesIcon,
  CreditCardIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { processPayment, PaymentPayload } from "../apis/payment";
import Modal from "../../src/components/Modal";

type PaymentMode = "upi" | "cash" | "card" | "cheque" | "online";

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  total: number;
  discount?: number;
  patientName?: string;
  patientId?: number;
  sourceType: "visit" | "pathology" | "pharmacy";
  sourceId?: number;
  allowPartialOrUnpaid?: boolean;
  testNames?: string[];
  selectedOrderIds?: number[];
  orderAmounts?: Record<number, number>;
  advanceBalance?: number;
  cashlessInsuranceAmount?: number;
  onSave?: (paymentData: PaymentData) => void;
  onPrint?: () => void;
  onPaymentSuccess?: () => void;
}

export interface PaymentData {
  upi: number;
  cash: number;
  card: number;
  cheque: number;
  online: number;
  paymentNote: string;
  transactionRef?: string;
  advanceUsed?: number;
  insuranceUsed?: number;
  roundOff?: number; // signed: positive = rounded up, negative = rounded down
}

const MODES: { key: PaymentMode; label: string; Icon: React.ComponentType<any> }[] = [
  { key: "upi", label: "UPI", Icon: DevicePhoneMobileIcon },
  { key: "cash", label: "Cash", Icon: BanknotesIcon },
  { key: "card", label: "Card", Icon: CreditCardIcon },
  { key: "cheque", label: "Cheque", Icon: DocumentTextIcon },
  { key: "online", label: "Online", Icon: GlobeAltIcon },
];

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  total,
  discount = 0,
  patientId = 0,
  sourceType,
  sourceId,
  allowPartialOrUnpaid = false,
  testNames = [],
  patientName,
  selectedOrderIds = [],
  orderAmounts = {},
  advanceBalance,
  cashlessInsuranceAmount,
  onSave,
  onPaymentSuccess,
}) => {
  const [selectedMode, setSelectedMode] = useState<PaymentMode>("cash");
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [transactionRef, setTransactionRef] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [advanceUsed, setAdvanceUsed] = useState(0);
  const [insuranceUsed, setInsuranceUsed] = useState(0);

  // Standard rounding: >=0.50 rounds up, <0.50 rounds down
  const calculateRoundOff = (amount: number) => {
    const rounded = Math.round(amount);
    return +(rounded - amount).toFixed(2);
  };

  const grossAmount = total - discount;
  const calculatedRoundOff = calculateRoundOff(grossAmount);
  const amountDue = grossAmount + calculatedRoundOff;
  const amountDueAfterAdvance = Math.max(amountDue - advanceUsed - insuranceUsed, 0);

  useEffect(() => {
    if (isOpen) {
      setSelectedMode("cash");
      setTransactionRef("");
      setPaymentNote("");
      setAdvanceUsed(0);
      setInsuranceUsed(0);
      setPaymentAmount(amountDue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleAdvanceChange = (value: string) => {
    const raw = parseFloat(value) || 0;
    const next = Math.min(Math.max(raw, 0), advanceBalance || 0);
    setAdvanceUsed(next);
    setPaymentAmount(Math.max(amountDue - next - insuranceUsed, 0));
  };

  const handleInsuranceChange = (value: string) => {
    const raw = parseFloat(value) || 0;
    const maxInsurance = Math.min(cashlessInsuranceAmount || 0, amountDue - advanceUsed);
    const next = Math.min(Math.max(raw, 0), maxInsurance);
    setInsuranceUsed(next);
    setPaymentAmount(Math.max(amountDue - advanceUsed - next, 0));
  };

  const effectivePaying = paymentAmount + advanceUsed + insuranceUsed;
  const balance = effectivePaying - amountDue;
  const isFullyPaid = balance >= 0;
  const canSave = allowPartialOrUnpaid || balance >= 0;

  const handleSave = async () => {
    if (!canSave) {
      message.error("Paying amount should not be less than amount due");
      return;
    }

    if (onSave) {
      const data: PaymentData = {
        upi: selectedMode === "upi" ? paymentAmount : 0,
        cash: selectedMode === "cash" ? paymentAmount : 0,
        card: selectedMode === "card" ? paymentAmount : 0,
        cheque: selectedMode === "cheque" ? paymentAmount : 0,
        online: selectedMode === "online" ? paymentAmount : 0,
        paymentNote,
        transactionRef: selectedMode !== "cash" ? transactionRef : undefined,
        advanceUsed,
        insuranceUsed,
        roundOff: calculatedRoundOff,
      };
      onSave(data);
      return;
    }

    try {
      const currentDate = new Date().toISOString().split("T")[0];
      const paymentModes: Array<{
        amount: number;
        payment_mode: string;
        payment_date: string;
        transaction_id?: string;
        notes?: string;
        allocation_type: "manual";
      }> = [];

      if (paymentAmount > 0) {
        paymentModes.push({
          amount: paymentAmount,
          payment_mode: selectedMode,
          payment_date: currentDate,
          transaction_id: selectedMode !== "cash" && transactionRef ? transactionRef : undefined,
          notes: paymentNote || undefined,
          allocation_type: "manual",
        });
      }
      if (advanceUsed > 0) {
        paymentModes.push({
          amount: advanceUsed,
          payment_mode: "cash",
          payment_date: currentDate,
          notes: "Advance allocation",
          allocation_type: "manual",
        });
      }
      if (insuranceUsed > 0) {
        paymentModes.push({
          amount: insuranceUsed,
          payment_mode: "insurance",
          payment_date: currentDate,
          notes: "Insurance cashless payment",
          allocation_type: "manual",
        });
      }

      const sourceIds =
        selectedOrderIds && selectedOrderIds.length > 0
          ? selectedOrderIds
          : sourceId
          ? [sourceId]
          : [];

      if (sourceIds.length === 0 && sourceType !== "pathology") {
        message.error("No orders selected for payment");
        return;
      }

      const mappedSourceType = sourceType === "pathology" ? "lab_order" : sourceType;

      const payload: PaymentPayload = {
        source_type: mappedSourceType as "lab_order" | "pharmacy_sale" | "visit",
        source_ids: sourceIds,
        payments: paymentModes,
        invoice_date: currentDate,
      };

      if (selectedOrderIds && selectedOrderIds.length > 1) {
        payload.source_amounts = {};
        selectedOrderIds.forEach((orderId) => {
          payload.source_amounts![orderId] = orderAmounts[orderId] || 0;
        });
      } else if (
        selectedOrderIds &&
        selectedOrderIds.length === 1 &&
        orderAmounts[selectedOrderIds[0]]
      ) {
        payload.source_amounts = {
          [selectedOrderIds[0]]: orderAmounts[selectedOrderIds[0]],
        };
      }

      await processPayment(payload);
      message.success("Payment processed successfully");
      onPaymentSuccess?.();
      onClose();
    } catch (err: any) {
      message.error(err?.message || "Payment failed. Please try again!");
    }
  };

  if (!isOpen) return null;

  const refPlaceholder =
    selectedMode === "upi"
      ? "UPI transaction ID / reference"
      : selectedMode === "card"
      ? "Card authorization / reference number"
      : selectedMode === "cheque"
      ? "Cheque number"
      : "Transaction reference number";

  return (
    <Modal
      title="Payment Details"
      width="sm:max-w-lg"
      onClose={onClose}
      modalContent={
        <div className="space-y-4">
          {/* Patient + Amount Due */}
          <div className="flex items-start justify-between">
            <div>
              {patientName && (
                <div className="font-bold text-gray-900 text-base">{patientName}</div>
              )}
              {testNames.length > 0 && (
                <div className="text-sm text-gray-500 mt-0.5">
                  {testNames.join(", ")}
                </div>
              )}
            </div>
            <div className="text-right flex-shrink-0 ml-4">
              <div className="text-xs uppercase tracking-wider text-gray-400 font-medium">
                Amount Due
              </div>
              <div className="text-2xl font-bold text-gray-900">
                ₹{amountDue.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Summary Card */}
          <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-3 gap-3">
            <div>
              <div className="text-xs text-gray-500 mb-1">Total</div>
              <div className="font-semibold text-gray-900">₹{total.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Discount</div>
              <div className="font-semibold text-red-500">– ₹{discount.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Round Off</div>
              <div className="font-semibold text-gray-700">
                {calculatedRoundOff >= 0 ? "+" : ""}₹{calculatedRoundOff.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Advance */}
          {typeof advanceBalance === "number" && advanceBalance > 0 && (
            <div className="flex items-center gap-3 bg-green-50 rounded-xl p-3">
              <div className="flex-1">
                <div className="text-xs text-gray-500">Advance Available</div>
                <div className="font-semibold text-green-700">₹{advanceBalance.toFixed(2)}</div>
              </div>
              <div className="flex-1">
                <div className="text-xs text-gray-500 mb-1">Use Advance</div>
                <input
                  type="number"
                  min={0}
                  max={advanceBalance}
                  value={advanceUsed || ""}
                  onChange={(e) => handleAdvanceChange(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
                  placeholder="0"
                />
              </div>
            </div>
          )}

          {/* Insurance */}
          {cashlessInsuranceAmount != null && cashlessInsuranceAmount > 0 && (
            <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-3">
              <div className="flex-1">
                <div className="text-xs text-gray-500">Insurance Approved</div>
                <div className="font-semibold text-blue-700">₹{cashlessInsuranceAmount.toFixed(2)}</div>
              </div>
              <div className="flex-1">
                <div className="text-xs text-gray-500 mb-1">Apply Insurance</div>
                <input
                  type="number"
                  min={0}
                  max={Math.min(cashlessInsuranceAmount, amountDue - advanceUsed)}
                  value={insuranceUsed || ""}
                  onChange={(e) => handleInsuranceChange(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
                  placeholder="0"
                />
              </div>
            </div>
          )}

          {/* Payment Mode */}
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400 font-medium mb-2">
              Payment Mode
            </div>
            <div className="grid grid-cols-5 gap-2">
              {MODES.map(({ key, label, Icon }) => {
                const isSelected = selectedMode === key;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      setSelectedMode(key);
                      setTransactionRef("");
                    }}
                    className={`relative flex flex-col items-center justify-center py-3 px-1 rounded-xl border-2 transition-all ${
                      isSelected
                        ? "border-primary bg-indigo-50"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    {isSelected && (
                      <CheckCircleIcon className="absolute top-1 right-1 w-4 h-4 text-primary" />
                    )}
                    <Icon className={`w-6 h-6 mb-1 ${isSelected ? "text-primary" : "text-gray-500"}`} />
                    <span className={`text-xs font-medium ${isSelected ? "text-primary" : "text-gray-600"}`}>
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Amount Input */}
          <div className="flex items-center border-2 border-gray-200 rounded-xl px-4 py-3 focus-within:border-primary transition-colors">
            <span className="text-gray-400 text-lg mr-2">₹</span>
            <input
              type="number"
              value={paymentAmount || ""}
              onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
              className="flex-1 outline-none text-xl font-semibold text-gray-900 bg-transparent"
              placeholder="0"
              min={0}
            />
          </div>

          {/* Transaction Reference (non-cash only) */}
          {selectedMode !== "cash" && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Transaction Reference</div>
              <input
                type="text"
                value={transactionRef}
                onChange={(e) => setTransactionRef(e.target.value)}
                placeholder={refPlaceholder}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          )}

          {/* Paying / Balance Summary */}
          <div
            className={`rounded-xl p-4 ${
              isFullyPaid ? "bg-green-50" : "bg-orange-50"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                  Paying
                </div>
                <div className="text-lg font-bold text-gray-900">
                  ₹{effectivePaying.toFixed(2)}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                  Balance
                </div>
                <div
                  className={`text-lg font-bold ${
                    isFullyPaid ? "text-green-600" : "text-orange-600"
                  }`}
                >
                  ₹{Math.abs(balance).toFixed(2)}
                </div>
              </div>
              <div
                className={`text-sm font-semibold px-4 py-1.5 rounded-full ${
                  isFullyPaid
                    ? "bg-green-100 text-green-700"
                    : "bg-orange-100 text-orange-600"
                }`}
              >
                {isFullyPaid ? "Fully paid" : "Pending"}
              </div>
            </div>
          </div>

          {/* Payment Note */}
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400 font-medium mb-2">
              Payment Note
            </div>
            <textarea
              rows={2}
              value={paymentNote}
              onChange={(e) => setPaymentNote(e.target.value)}
              placeholder="Enter any additional payment notes..."
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-primary transition-colors"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-6 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              className="px-6 py-2 text-sm font-semibold text-white bg-primary rounded-full hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              Save Payment
            </button>
          </div>
        </div>
      }
    />
  );
};

export default PaymentModal;
